import React, { useState, useEffect } from "react";
import {
  Container,
  Button,
  Typography,
  CircularProgress,
  Box,
  Card,
  CardContent,
  Divider,
  AppBar,
  Toolbar,
  Grid,
  Checkbox,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import api from '../services/api';
import { useAuthContext } from "../services/AuthContext";
import { isAuthenticated } from "../services/auth";
import logo from "../assets/logo-white.png";
import heroImage1 from "../assets/heroimage1.png";
import whatHappensNextIcon from "../assets/Group 42243.png";
import serviceSelectionIcon from "../assets/Group 42242.png";
import bookingIcon from "../assets/Group 42241.png";
// Progress bar assets
import ppicon1 from "../assets/prograssbar/ppicon1.png";
import ppline1 from "../assets/prograssbar/ppline1.png";
import ppline12 from "../assets/prograssbar/ppline12.png";
import ppicon12 from "../assets/prograssbar/ppicon12.png";
import ppicon13 from "../assets/prograssbar/ppicon13.png";
import ppline2 from "../assets/prograssbar/ppline2.png";
import ppicon2 from "../assets/prograssbar/ppicon2.png";
import ppline21 from "../assets/prograssbar/ppline21.png";
import ppline3 from "../assets/prograssbar/ppline3.png";
import ppline31 from "../assets/prograssbar/ppline31.png";
import ppicon3 from "../assets/prograssbar/ppicon3.png";

function Payment() {
  const [amount, setAmount] = useState(0);
  const [gstAmount, setGstAmount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [bookingFee, setBookingFee] = useState(0);
  const [bookingFeeGst, setBookingFeeGst] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState("selectBlockFormula");
  const [selectedOption, setSelectedOption] = useState("buyNow");
  const [isBooking, setIsBooking] = useState(false);
  const [additionalServices, setAdditionalServices] = useState({
    marketing: false,
    manufacturing: false,
  });
  const [callSchedule, setCallSchedule] = useState({ date: "", time: "" });
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedClaimDetails, setSelectedClaimDetails] = useState([]);
  const { user, token, logout: authLogout, loading: authLoading } = useAuthContext();
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const getErrorMessage = (error) => {
    if (!error) return "An unknown error occurred.";
    if (error.response?.status === 401) {
      authLogout();
      navigate("/login");
      return "Session expired. Please log in again.";
    }
    if (error.response?.data?.detail) {
      const detail = error.response.data.detail;
      if (Array.isArray(detail)) {
        return detail.map((err) => `${err.loc?.join('.')}: ${err.msg}`).join("; ");
      }
      return detail;
    }
    return error.message || "An unexpected error occurred.";
  };

  useEffect(() => {
    const claims = location.state?.selectedClaimDetails || [];
    const validClaims = claims.filter(
      (claim) => claim?.id && Number.isInteger(Number(claim.id)) && claim.amount && claim.booking_fee
    );
    if (!validClaims.length) {
      setError("No valid claims selected or claims missing required fields.");
      navigate("/dashboard");
      return;
    }
    setSelectedClaimDetails(validClaims);
    console.log("Validated selectedClaimDetails:", validClaims);
  }, [location.state, navigate]);

  useEffect(() => {
    if (authLoading) return;
    const storedToken = localStorage.getItem("token");
    if (!user || !token || !isAuthenticated() || !storedToken) {
      console.log("Authentication check failed, redirecting to login");
      setError("Session expired or not authenticated.");
      authLogout();
      navigate("/login");
    }
  }, [user, token, authLogout, navigate, authLoading]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const orderId = queryParams.get("order_id");
    if (orderId) {
      const fetchOrderDetails = async () => {
        setIsLoading(true);
        try {
          const response = await api.get('/orders/order-history', {
            headers: { Authorization: `Bearer ${token}` },
          });
          const order = response.data.orders.find((o) => o.id === orderId);
          if (!order || order.status !== "booking_paid") {
            throw new Error("Invalid or non-booking order.");
          }
          const claimResponse = await api.get(`/label_claims/?id=${order.label_claim_id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const claim = claimResponse.data[0];
          if (!claim || !claim.id || !Number.isInteger(Number(claim.id))) {
            throw new Error("Invalid label claim data.");
          }
          setSelectedClaimDetails([claim]);
          setCurrentOrderId(orderId);
          setIsBooking(false);
          setStep("selectBlockFormula");
          setSelectedOption("buyNow");
          console.log("Set currentOrderId from URL:", orderId);
        } catch (error) {
          console.error("Failed to fetch order details:", error);
          setError(getErrorMessage(error));
        } finally {
          setIsLoading(false);
        }
      };
      fetchOrderDetails();
    }
  }, [location.search, token]);

  useEffect(() => {
    if (!selectedClaimDetails.length) return;

    try {
      const baseAmount = selectedClaimDetails.reduce((acc, claim) => {
        const claimAmount = parseFloat(claim.amount) || 0;
        return acc + claimAmount;
      }, 0);

      const baseBookingFee = selectedClaimDetails.reduce((acc, claim) => {
        const bookingFee = parseFloat(claim.booking_fee) || 0;
        return acc + bookingFee;
      }, 0);

      const marketingAmount = 0;
      const manufacturingAmount = 0;

      const gstRate = 0.18;
      const totalBaseAmountExclGst = baseAmount + marketingAmount + manufacturingAmount;
      const calculatedGst = totalBaseAmountExclGst * gstRate;
      const totalAmountInclGst = totalBaseAmountExclGst + calculatedGst;

      const bookingFeeGst = baseBookingFee * gstRate;
      const bookingFeeInclGst = baseBookingFee + bookingFeeGst;

      setAmount(totalAmountInclGst.toFixed(2));
      setGstAmount(calculatedGst.toFixed(2));
      setTotalAmount(totalAmountInclGst.toFixed(2));
      setBookingFee(bookingFeeInclGst.toFixed(2));
      setBookingFeeGst(bookingFeeGst.toFixed(2));
    } catch (error) {
      console.error("Amount calculation error:", error);
      setError("Failed to calculate amounts.");
    }
  }, [selectedClaimDetails, additionalServices]);

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    const isBookingOption = option === "bookNow";
    setIsBooking(isBookingOption);
    console.log("Selected option:", option, "isBooking:", isBookingOption);
  };

  const handleServiceToggle = (service) => {
    setAdditionalServices((prev) => ({
      marketing: service === "marketing" ? true : false,
      manufacturing: service === "manufacturing" ? true : false,
    }));
  };

  const handleNext = () => {
    if (step === "selectBlockFormula") {
      setStep("serviceSelection");
    } else if (step === "serviceSelection") {
      setStep("paymentDetails");
    }
  };

  const handleSkip = () => {
    setAdditionalServices({ marketing: false, manufacturing: false });
    setStep("paymentDetails");
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve, reject) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => {
        console.error("Failed to load Razorpay script");
        reject(new Error("Failed to load Razorpay SDK"));
      };
      const timeout = setTimeout(() => {
        reject(new Error("Razorpay script loading timed out. Please check your network and try again."));
      }, 10000);
      script.onload = () => {
        clearTimeout(timeout);
        resolve(true);
      };
      script.onerror = () => {
        clearTimeout(timeout);
        reject(new Error("Failed to load Razorpay SDK"));
      };
      document.body.appendChild(script);
    });
  };

  const handlePayment = async (isFullPayment = false, orderIdForRemaining = null) => {
    const storedToken = localStorage.getItem("token");
    if (!isAuthenticated() || !storedToken || !user?.id || !user?.email) {
      console.log("Payment failed: User not authenticated, redirecting to login");
      setError("User not authenticated.");
      authLogout();
      navigate("/login");
      return;
    }

    try {
      const payload = JSON.parse(atob(storedToken.split(".")[1]));
      if (payload.exp * 1000 < Date.now()) {
        console.log("Token expired during payment, redirecting to login");
        setError("Session expired. Please log in again.");
        authLogout();
        navigate("/login");
        return;
      }
    } catch (error) {
      console.error("Invalid token format during payment:", error);
      setError("Invalid token. Please log in again.");
      authLogout();
      navigate("/login");
      return;
    }

    if (!selectedClaimDetails.length) {
      setError("No claims selected.");
      alert("No claims selected.");
      return;
    }

    if (!termsAccepted) {
      setError("Please accept the terms and conditions to proceed.");
      alert("Please accept the terms and conditions to proceed.");
      return;
    }

    let totalPayment;
    if (orderIdForRemaining) {
      const response = await api.get('/orders/order-history', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const order = response.data.orders.find((o) => o.id === orderIdForRemaining);
      totalPayment = order.remaining_amount;
    } else {
      totalPayment = selectedOption === "bookNow" && !isFullPayment ? bookingFee : isFullPayment ? (totalAmount - bookingFee) : totalAmount;
    }

    if (!totalPayment || isNaN(parseFloat(totalPayment)) || parseFloat(totalPayment) <= 0) {
      setError("Invalid payment amount.");
      alert("Invalid payment amount.");
      return;
    }

    setIsPaymentLoading(true);
    setError(null);

    try {
      for (const claim of selectedClaimDetails) {
        const response = await api.get(`/label_claims/?id=${claim.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const claimData = response.data[0];
        if (!claimData || claimData.is_blocked) {
          throw new Error(`Claim ID ${claim.id} is not available or blocked.`);
        }
        if (selectedOption === "bookNow" && !isFullPayment && !orderIdForRemaining) {
          const orderResponse = await api.get('/orders/order-history', {
            headers: { Authorization: `Bearer ${token}` },
          });
          const existingOrder = orderResponse.data.orders.find(
            (o) => o.label_claim_id === Number(claim.id) && o.status === "fully_paid"
          );
          if (existingOrder) {
            throw new Error(`Claim ID ${claim.id} is already fully paid. Cannot process booking payment.`);
          }
        }
      }

      const isRazorpayLoaded = await loadRazorpayScript();
      if (!isRazorpayLoaded) {
        throw new Error("Failed to load Razorpay SDK.");
      }

      if (!process.env.REACT_APP_RAZORPAY_KEY_ID) {
        throw new Error("Razorpay key is not configured.");
      }

      console.log("Processing claims:", JSON.stringify(selectedClaimDetails, null, 2));
      console.log("Payment state:", { selectedOption, isBooking, isFullPayment, totalPayment, bookingFee, totalAmount });

      const processClaim = async (index) => {
        if (index >= selectedClaimDetails.length) {
          setIsPaymentLoading(false);
          return;
        }

        const claim = selectedClaimDetails[index];
        if (!claim.id || !Number.isInteger(Number(claim.id))) {
          setError(`Invalid claim ID: ${claim.id || "undefined"} at index ${index}`);
          alert(`Invalid claim ID: ${claim.id || "undefined"}`);
          setIsPaymentLoading(false);
          return;
        }

        let razorpayOrder;
        if (orderIdForRemaining) {
          console.log("Calling /orders/" + orderIdForRemaining + "/pay-remaining");
          const response = await api.post(
            `/orders/${orderIdForRemaining}/pay-remaining`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          razorpayOrder = response.data.razorpay_order;
          setCurrentOrderId(orderIdForRemaining);
          console.log("Updated currentOrderId for remaining payment:", orderIdForRemaining);
        } else {
          const paymentType = selectedOption === "bookNow" && !isFullPayment ? "booking" : "full";
          const requestBody = {
            label_claim_id: Number(claim.id),
            payment_type: paymentType,
          };

          console.log("Sending request to orders/:", JSON.stringify(requestBody, null, 2));

          const response = await api.post('orders/', requestBody, {
            headers: { Authorization: `Bearer ${token}` },
          });

          razorpayOrder = response.data.order;
          setCurrentOrderId(razorpayOrder.id);
          console.log("Updated currentOrderId for new order:", razorpayOrder.id);
        }

        if (!razorpayOrder?.id) {
          throw new Error(`Order creation failed for claim ID ${claim.id}`);
        }

        console.log("Opening Razorpay checkout for order:", razorpayOrder.id);
        const options = {
          key: process.env.REACT_APP_RAZORPAY_KEY_ID,
          amount: razorpayOrder.amount,
          currency: razorpayOrder.currency,
          order_id: razorpayOrder.id,
          name: "DealSphere",
          description: `Payment for Label Claim ID ${claim.id} (${orderIdForRemaining ? "remaining" : selectedOption === "bookNow" && !isFullPayment ? "booking" : "full"})`,
          handler: async function (response) {
            try {
              const paymentSuccessResponse = await api.post(
                '/orders/payment-success',
                {
                  payment_id: response.razorpay_payment_id,
                  order_id: response.razorpay_order_id,
                  label_claim_id: claim.id,
                  amount: razorpayOrder.amount / 100,
                },
                {
                  headers: { Authorization: `Bearer ${token}` },
                }
              );
              console.log("Payment success:", JSON.stringify(paymentSuccessResponse.data, null, 2));
              console.log("Payment completed. orderIdForRemaining:", orderIdForRemaining, "selectedOption:", selectedOption, "isFullPayment:", isFullPayment);
              if (orderIdForRemaining) {
                console.log("Remaining payment completed, navigating to finalSuccess");
                navigate("/final-success", { state: { selectedClaimDetails } });
              } else if (selectedOption === "bookNow" && !isFullPayment) {
                console.log("Booking payment completed, navigating to paymentSuccess");
                navigate("/payment-success", { state: { selectedClaimDetails, currentOrderId: razorpayOrder.id } });
              } else {
                console.log("Full payment completed, navigating to finalSuccess");
                navigate("/final-success", { state: { selectedClaimDetails } });
              }
              setCurrentOrderId(null);
              await processClaim(index + 1);
            } catch (error) {
              console.error("Payment success error:", error);
              const errorMessage = getErrorMessage(error);
              setError(`Failed to verify payment for claim ID ${claim.id}: ${errorMessage}`);
              alert(`Payment verification failed: ${errorMessage}`);
              setIsPaymentLoading(false);
              setCurrentOrderId(null);
            }
          },
          prefill: {
            email: user.email,
            contact: user.contact || "",
          },
          theme: {
            color: "#66202D",
          },
        };

        const rzp = new window.Razorpay(options);
        rzp.on("payment.failed", function (response) {
          console.error("Payment failed:", response.error);
          setError(`Payment canceled or failed: ${response.error.description}`);
          alert(`Payment canceled or failed: ${response.error.description}`);
          setIsPaymentLoading(false);
          setCurrentOrderId(null);
        });
        rzp.on("payment.cancel", function () {
          console.log("Payment canceled by user");
          setError("Payment was canceled. The order remains pending.");
          alert("Payment was canceled. The order remains pending.");
          setIsPaymentLoading(false);
          setCurrentOrderId(null);
        });
        rzp.open();
      };

      await processClaim(0);
    } catch (error) {
      console.error("Payment initialization error:", error);
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
      alert(`Payment failed: ${errorMessage}`);
      setIsPaymentLoading(false);
      setCurrentOrderId(null);
    }
  };

  const handleScheduleCall = async () => {
    if (!callSchedule.date || !callSchedule.time) {
      setError("Please select a date and time for the call.");
      alert("Please select a date and time.");
      return;
    }

    setIsLoading(true);
    try {
      const orderIds = await Promise.all(
        selectedClaimDetails.map(async (claim) => {
          const response = await api.get('/orders/order-history', {
            headers: { Authorization: `Bearer ${token}` },
          });
          const order = response.data.orders.find(
            (o) => o.label_claim_id === Number(claim.id)
          );
          if (!order) {
            console.warn(`No order found for claim ID ${claim.id}`);
            return null;
          }
          if (order.status === "fully_paid") {
            console.log(`Claim ID ${claim.id} is fully paid. Navigating to finalSuccess.`);
            navigate("/final-success", { state: { selectedClaimDetails } });
            return null;
          }
          if (order.status !== "booking_paid") {
            console.warn(`No booking_paid order found for claim ID ${claim.id}. Current status: ${order.status}`);
            return null;
          }
          return order.id;
        })
      );

      const validOrderIds = orderIds.filter((id) => id);
      if (!validOrderIds.length) {
        const claimIds = selectedClaimDetails.map((c) => c.id).join(", ");
        throw new Error(`No valid booking orders found for claim IDs: ${claimIds}.`);
      }

      await api.post(
        '/orders/schedule-call',
        {
          order_ids: validOrderIds,
          date: callSchedule.date,
          time: callSchedule.time,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setCurrentOrderId(validOrderIds[0]);
      console.log("Updated currentOrderId after scheduling call:", validOrderIds[0]);
      navigate("/call-schedule-success", { state: { selectedClaimDetails, currentOrderId: validOrderIds[0] } });
    } catch (error) {
      console.error("Failed to schedule call:", error);
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
      alert(`Failed to schedule call: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayRemaining = async (orderId) => {
    if (!orderId) {
      setError("Order ID is missing. Please try again.");
      alert("Order ID is missing. Please try again.");
      return;
    }

    setIsPaymentLoading(true);
    setError(null);
    try {
      console.log("Initiating remaining payment for order ID:", orderId);
      await handlePayment(false, orderId);
    } catch (error) {
      console.error("Failed to initiate remaining payment:", error);
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
      alert(`Failed to initiate payment. Please try again: ${errorMessage}`);
    } finally {
      setIsPaymentLoading(false);
    }
  };

  const getOrderIdFromUrl = () => {
    const searchParams = new URLSearchParams(location.search);
    const orderId = searchParams.get("order_id");
    console.log("Extracted orderId from URL:", orderId);
    return orderId;
  };

  const handleCancel = () => {
    setCurrentOrderId(null);
    setError(null);
    navigate("/dashboard");
  };

  const handleLogout = () => {
    authLogout();
    navigate("/login");
  };

  const orderIdForPayRemaining = currentOrderId || getOrderIdFromUrl();

  if (authLoading || isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  // Progress bar rendering based on the current step
  const steps = [
    { label: "What Happens Next", icon: ppicon1, activeIcon: ppicon1 },
    { label: "Service Selection", icon: ppicon12, activeIcon: ppicon2 },
    { label: "Blocking", icon: ppicon13, activeIcon: ppicon3 },
  ];

  const currentStepIndex = step === "selectBlockFormula" ? 0 : step === "serviceSelection" ? 1 : step === "paymentDetails" ? 2 : -1;

  const renderProgressBar = () => {
    return (
      <Box sx={{ width: "1170px", margin: "50px auto", mb: 4, position: "relative" }}>
        {/* Progress Line */}
        <Box
          sx={{
            position: "absolute",
            top: "25px",
            left: 0,
            right: 0,
            height: "4px",
            backgroundColor: "#E0E0E0",
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              width: `${((currentStepIndex + 1) / steps.length) * 100}%`,
              height: "100%",
              backgroundColor: "#F5A623",
              transition: "width 0.3s ease",
            }}
          />
        </Box>

        {/* Icons */}
        <Box sx={{ display: "flex", justifyContent: "space-between", position: "relative", zIndex: 2 }}>
          {steps.map((s, index) => (
            <Box key={index} sx={{ textAlign: "center", width: "33.33%" }}>
              <img
                src={index <= currentStepIndex ? s.activeIcon : s.icon}
                alt={`Step ${index + 1}`}
                style={{ width: "50px", height: "50px", background: "#fff", borderRadius: "50%", zIndex: 2 }}
              />
            </Box>
          ))}
        </Box>

        {/* Labels */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
          {steps.map((s, index) => (
            <Box key={index} sx={{ textAlign: "center", width: "33.33%" }}>
              <Typography sx={{ fontFamily: "'PP Neue Montreal', 'Inter', sans-serif", fontSize: "18px", fontWeight: 500, color: "#1E1E1E" }}>
                {s.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        background: "#FFFFFF",
      }}
    >
      <AppBar
        position="sticky"
        sx={{
          background: `url(${heroImage1}) no-repeat center/cover`,
          boxShadow: "none",
          padding: "10px 117px",
          height: "260px",
          zIndex: 1,
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingTop: "14px",
          }}
        >
          <img
            src={logo}
            alt="Logo"
            style={{
              width: "118px",
              height: "67px",
              objectFit: "contain",
            }}
          />
          <Box
            sx={{
              display: "flex",
              gap: "20px",
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            <Button
              color="inherit"
              onClick={() => navigate("/dashboard")}
              sx={{
                color: "#FFFFFF",
                textTransform: "none",
                fontSize: "21px",
                fontWeight: 400,
                lineHeight: "200%",
                padding: "8px 16px",
                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
              }}
            >
              Label Claims
            </Button>
            <Button
              color="inherit"
              onClick={() => navigate("/dashboard?view=orderDetails")}
              sx={{
                color: "#FFFFFF",
                textTransform: "none",
                fontSize: "21px",
                fontWeight: 400,
                lineHeight: "200%",
                padding: "8px 16px",
                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
              }}
            >
              Order Details
            </Button>
            <Button
              color="inherit"
              onClick={() => navigate("/user-settings")}
              sx={{
                color: "#FFFFFF",
                textTransform: "none",
                fontSize: "21px",
                fontWeight: 400,
                lineHeight: "200%",
                padding: "8px 16px",
                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
              }}
            >
              User Settings
            </Button>
          </Box>
          <Button
            color="inherit"
            onClick={handleLogout}
            sx={{
              color: "#FFFFFF",
              textTransform: "uppercase",
              fontSize: "16px",
              fontWeight: 700,
              fontFamily: "'Lato', sans-serif",
              lineHeight: "200%",
              padding: "8px 16px",
              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
            }}
          >
            Logout
          </Button>
        </Toolbar>
        <Box
          sx={{
            height: "calc(260px - 64px)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "#FFFFFF",
            textAlign: "center",
            marginBottom: "50px",
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 500,
              fontSize: "48px",
              lineHeight: "90%",
              fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
              textTransform: "none",
            }}
          >
            Book Label Claim
          </Typography>
        </Box>
      </AppBar>

      <Grid container sx={{ flex: 1, padding: "0px 117px", justifyContent: "center", marginTop: "-70px", zIndex: 2 }}>
        <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", }}>
          <Box
            sx={{
              backgroundColor: "#FFFFFF",
              borderRadius: "16px",
              border: "1px solid #ECECEC",
              boxShadow: "0px 14px 24px rgba(0, 0, 0, 0.05)",
              padding: "30px",
              width: "1366px",
              height: "fit-content",
              position: "relative",
              
            }}
          >
            {error && (
              <Typography
                sx={{
                  color: "red",
                  fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                  fontSize: "16px",
                  marginBottom: "20px",
                }}
              >
                {error}
              </Typography>
            )}
            <Box
              sx={{
                width: "1170px",
                background: "#FBFBFB",
                border: "1px solid #F3F3F3",
                boxShadow: "0px 4px 14px rgba(0, 0, 0, 0.12)",
                borderRadius: "14px",
                padding: "20px",
                margin: "0 auto",
              }}
            >
              {selectedClaimDetails.length > 0 ? (
  selectedClaimDetails.map((claim, index) => (
    <Box key={claim.id || `claim-${index}`}>
      {/* Add a ref to the container to check its width */}
      <Box
        ref={(el) => {
          if (el) {
            console.log("Container width:", el.offsetWidth);
          }
        }}
        sx={{ minWidth: "1200px" }} // Ensure the container is wide enough for three columns
      >
        <Grid
          container
          spacing={0}
          sx={{
            flexWrap: "nowrap", // Prevent wrapping of columns
            width: "100%", // Ensure the grid takes the full width of the parent
            paddingRight: "40px",
            PaddingLeft:"20px"
          }}
        >
          {/* Left Column */}
          <Grid item xs={4} sx={{ paddingRight: "20px", minWidth: "33.33%" }}>
            <Typography
              sx={{
                fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                fontSize: "18px",
                fontWeight: 400,
                color: "#1E1E1E",
                marginBottom: "4px",
              }}
            >
              {claim.grade || "N/A"}
            </Typography>
            <Divider sx={{ borderColor: "#FFC107", margin: "8px 0" }} />
            <Typography
              sx={{
                fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                fontSize: "14px",
                fontWeight: 400,
                color: "#AEA9A6",
                marginBottom: "4px",
              }}
            >
              Condition:
            </Typography>
            <Typography
              sx={{
                fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                fontSize: "18px",
                fontWeight: 400,
                color: "#1E1E1E",
                marginBottom: "4px",
              }}
            >
              {claim.condition || "N/A"}
            </Typography>
            <Divider sx={{ borderColor: "#FFC107", margin: "8px 0" }} />
            <Typography
              sx={{
                fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                fontSize: "14px",
                fontWeight: 400,
                color: "#AEA9A6",
                marginBottom: "4px",
              }}
            >
              Health Claims:
            </Typography>
            <Typography
              sx={{
                fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                fontSize: "18px",
                fontWeight: 400,
                color: "#1E1E1E",
                marginBottom: "4px",
              }}
            >
              {claim.health_claims || "N/A"}
            </Typography>
            <Divider sx={{ borderColor: "#FFC107", margin: "8px 0" }} />
            <Typography
              sx={{
                fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                fontSize: "14px",
                fontWeight: 400,
                color: "#AEA9A6",
                marginBottom: "4px",
              }}
            >
              Clinical Trials:
            </Typography>
            <Typography
              sx={{
                fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                fontSize: "18px",
                fontWeight: 400,
                color: claim.clinical_trials === 1 ? "#13B10B" : "#E40C0C",
                marginBottom: "4px",
              }}
            >
              {claim.clinical_trials === 1 ? "Yes" : "No"}
            </Typography>
            <Divider sx={{ borderColor: "#FFC107", margin: "8px 0" }} />
            <Typography
              sx={{
                fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                fontSize: "14px",
                fontWeight: 400,
                color: "#AEA9A6",
                marginBottom: "4px",
              }}
            >
              Dosage:
            </Typography>
            <Typography
              sx={{
                fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                fontSize: "18px",
                fontWeight: 400,
                color: "#1E1E1E",
                marginBottom: "4px",
              }}
            >
              {claim.dosage || "N/A"}
            </Typography>
            <Divider sx={{ borderColor: "#FFC107", margin: "8px 0" }} />
            <Typography
              sx={{
                fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                fontSize: "14px",
                fontWeight: 400,
                color: "#AEA9A6",
                marginBottom: "4px",
              }}
            >
              Label:
            </Typography>
            <Typography
              sx={{
                fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                fontSize: "18px",
                fontWeight: 400,
                color: "#1E1E1E",
              }}
            >
              {claim.label_claim || "N/A"}
            </Typography>
            <Divider sx={{ borderColor: "#FFC107", margin: "8px 0" }} />
          </Grid>

          {/* Middle Column */}
          <Grid item xs={4} sx={{ paddingRight: "20px", minWidth: "33.33%" }}>
            <Typography
              sx={{
                fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                fontSize: "18px",
                fontWeight: 400,
                color: "#1E1E1E",
                marginBottom: "4px",
              }}
            >
              {claim.label_claim || "N/A"}
            </Typography>
            <Divider sx={{ borderColor: "#FFC107", margin: "8px 0" }} />
            <Typography
              sx={{
                fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                fontSize: "14px",
                fontWeight: 400,
                color: "#AEA9A6",
                marginBottom: "4px",
              }}
            >
              Prototype:
            </Typography>
            <Typography
              sx={{
                fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                fontSize: "18px",
                fontWeight: 400,
                color: claim.prototype === 1 ? "#13B10B" : "#E40C0C",
                marginBottom: "4px",
              }}
            >
              {claim.prototype === 1 ? "Yes" : "No"}
            </Typography>
            <Divider sx={{ borderColor: "#FFC107", margin: "8px 0" }} />
            <Typography
              sx={{
                fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                fontSize: "14px",
                fontWeight: 400,
                color: "#AEA9A6",
                marginBottom: "4px",
              }}
            >
              Regulatory:
            </Typography>
            <Typography
              sx={{
                fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                fontSize: "18px",
                fontWeight: 400,
                color: claim.regulatory === 1 ? "#13B10B" : "#E40C0C",
                marginBottom: "4px",
              }}
            >
              {claim.regulatory === 1 ? "Yes" : "No"}
            </Typography>
            <Divider sx={{ borderColor: "#FFC107", margin: "8px 0" }} />
            <Typography
              sx={{
                fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                fontSize: "14px",
                fontWeight: 400,
                color: "#AEA9A6",
                marginBottom: "4px",
              }}
            >
              Format:
            </Typography>
            <Typography
              sx={{
                fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                fontSize: "18px",
                fontWeight: 400,
                color: "#1E1E1E",
                marginBottom: "4px",
              }}
            >
              {claim.format || "N/A"}
            </Typography>
            <Divider sx={{ borderColor: "#FFC107", margin: "8px 0" }} />
            <Typography
              sx={{
                fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                fontSize: "14px",
                fontWeight: 400,
                color: "#AEA9A6",
                marginBottom: "4px",
              }}
            >
              Total Amount (Incl. GST):
            </Typography>
            <Typography
              sx={{
                fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                fontSize: "18px",
                fontWeight: 400,
                color: "#1E1E1E",
                marginBottom: "4px",
              }}
            >
              ₹{parseFloat(claim.amount) > 0 ? claim.amount : "0.00"}
            </Typography>
            <Divider sx={{ borderColor: "#FFC107", margin: "8px 0" }} />
            <Typography
              sx={{
                fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                fontSize: "14px",
                fontWeight: 400,
                color: "#AEA9A6",
                marginBottom: "4px",
              }}
            >
              Booking Fee (Incl. GST):
            </Typography>
            <Typography
              sx={{
                fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                fontSize: "18px",
                fontWeight: 400,
                color: "#1E1E1E",
                marginBottom: "4px",
              }}
            >
              ₹{parseFloat(claim.booking_fee) > 0 ? claim.booking_fee : "0.00"}
            </Typography>
            <Divider sx={{ borderColor: "#FFC107", margin: "8px 0" }} />
          </Grid>

          {/* Right Column */}
          <Grid item xs={4} sx={{ minWidth: "33.33%" }}>
            <Typography
              sx={{
                fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                fontSize: "18px",
                fontWeight: 400,
                color: "#1E1E1E",
                marginBottom: "4px",
              }}
            >
              Description
            </Typography>
            <Divider sx={{ borderColor: "#FFC107", margin: "8px 0" }} />
            <Typography
              sx={{
                fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                fontSize: "18px",
                fontWeight: 400,
                color: "#1E1E1E",
                marginBottom: "20px",
              }}
            >
              {claim.description || "N/A"}
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
              <Button
                sx={{
                  border: "2px solid #66202D",
                  borderRadius: "50px",
                  padding: "10px 20px",
                  fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#66202D",
                  textTransform: "uppercase",
                  "&:hover": {
                    backgroundColor: "#FFF8F5",
                    boxShadow: "0px 2px 8px rgba(102, 32, 45, 0.2)",
                  },
                }}
              >
                Download Sample
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  ))
) : (
  <Typography
    sx={{
      fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
      fontSize: "18px",
      fontWeight: 400,
      color: "#1E1E1E",
      textAlign: "center",
    }}
  >
    No claim details available.
  </Typography>
)}
            </Box>

            {renderProgressBar()}

            {step === "selectBlockFormula" && (
              <Box sx={{ marginTop: "40px", width: "1170px", margin: "0 auto" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "25px" }}>
                </Box>
                <Box
                  sx={{
                    background: "#FBFBFB",
                    borderRadius: "20px",
                    padding: "30px",
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
                    border: "1px solid #E0E0E0",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                      fontSize: "22px",
                      fontWeight: 600,
                      color: "#1E1E1E",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Select Block the Formula
                  </Typography>
                  <Box
                    sx={{
                      padding: "20px",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "20px",
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: "15px",
                        padding: "20px",
                        background: selectedOption === "bookNow" ? "#FFF8F5" : "transparent",
                        borderRadius: "12px",
                        cursor: "pointer",
                        border: selectedOption === "bookNow" ? "1px solid #FFA146" : "1px solid #D3D3D3",
                        boxShadow: selectedOption === "bookNow" ? "0px 2px 8px rgba(102, 32, 45, 0.15)" : "none",
                        transition: "all 0.3s ease",
                      }}
                      onClick={() => handleOptionSelect("bookNow")}
                    >
                      <Radio
                        checked={selectedOption === "bookNow"}
                        onChange={() => handleOptionSelect("bookNow")}
                        sx={{ "& .MuiSvgIcon-root": { fontSize: "26px", color: selectedOption === "bookNow" ? "#66202D" : "#B0B0B0" } }}
                      />
                      <Box>
                        <Typography
                          sx={{
                            fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                            fontSize: "18px",
                            fontWeight: 500,
                            color: selectedOption === "bookNow" ? "#66202D" : "#333333",
                          }}
                        >
                          Book Now
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                            fontSize: "14px",
                            fontWeight: 400,
                            color: "#888888",
                          }}
                        >
                          Pay ₹{bookingFee} (Incl of GST) now and the remaining later
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                            fontSize: "14px",
                            fontWeight: 400,
                            color: "#888888",
                            marginTop: "10px",
                          }}
                        >
                          You can book now Lorem ipsum dolor sit amet, consectetur adipiscing elit. Phasellus convallis maximus orci sit amet finibus. Nam a convallis felis, tempus tincidunt mi. Proin et erat at leo bibendum faucibus. Phasellus hendrerit telllus non faucibus posuere.
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: "15px",
                        padding: "20px",
                        background: selectedOption === "buyNow" ? "#FFF8F5" : "transparent",
                        borderRadius: "12px",
                        cursor: "pointer",
                        border: selectedOption === "buyNow" ? "1px solid #FFA146" : "1px solid #D3D3D3",
                        boxShadow: selectedOption === "buyNow" ? "0px 2px 8px rgba(102, 32, 45, 0.15)" : "none",
                        transition: "all 0.3s ease",
                      }}
                      onClick={() => handleOptionSelect("buyNow")}
                    >
                      <Radio
                        checked={selectedOption === "buyNow"}
                        onChange={() => handleOptionSelect("buyNow")}
                        sx={{ "& .MuiSvgIcon-root": { fontSize: "26px", color: selectedOption === "buyNow" ? "#66202D" : "#B0B0B0" } }}
                      />
                      <Box>
                        <Typography
                          sx={{
                            fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                            fontSize: "18px",
                            fontWeight: 500,
                            color: selectedOption === "buyNow" ? "#66202D" : "#333333",
                          }}
                        >
                          Buy Now
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                            fontSize: "14px",
                            fontWeight: 400,
                            color: "#888888",
                          }}
                        >
                          Pay ₹{totalAmount} (Incl GST) now
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                            fontSize: "14px",
                            fontWeight: 400,
                            color: "#888888",
                            marginTop: "10px",
                          }}
                        >
                          You can buy now Lorem ipsum dolor sit amet, consectetur adipiscing elit. Phasellus convallis maximus orci sit amet finibus. Nam a convallis felis, tempus tincidunt mi. Proin et erat at leo bibendum faucibus. Phasellus hendrerit telllus non faucibus posuere.
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "end", gap: "25px", marginTop: "40px" }}>
                  <Button
                    onClick={handleCancel}
                    sx={{
                      border: "2px solid #66202D",
                      borderRadius: "50px",
                      padding: "12px 40px",
                      fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "#66202D",
                      textTransform: "uppercase",
                      backgroundColor: "transparent",
                      "&:hover": {
                        backgroundColor: "#FFF8F5",
                        boxShadow: "0px 2px 8px rgba(102, 32, 45, 0.2)",
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleNext}
                    sx={{
                      backgroundColor: "#66202D",
                      color: "#FFFFFF",
                      borderRadius: "50px",
                      padding: "12px 40px",
                      fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                      fontSize: "16px",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      boxShadow: "0px 2px 8px rgba(102, 32, 45, 0.3)",
                      "&:hover": {
                        backgroundColor: "#551A25",
                        boxShadow: "0px 4px 12px rgba(102, 32, 45, 0.4)",
                      },
                    }}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            )}

            {step === "serviceSelection" && (
              <Box sx={{ marginTop: "40px", width: "1170px", margin: "0 auto" }}>
                <Box
                  sx={{
                    backgroundColor: "#FBFBFB",
                    borderRadius: "16px",
                    border: "1px solid #ECECEC",
                    boxShadow: "0px 14px 24px rgba(0, 0, 0, 0.05)",
                    padding: "30px",
                    height: "fit-content",
                    position: "relative",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "25px" }}>
                    <Typography
                      sx={{
                        fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                        fontSize: "22px",
                        fontWeight: "600",
                        color: "#1E1E1E",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Do You Want to Purchase Anything Else?
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      borderRadius: "12px",
                      padding: "20px",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "20px",
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: "15px",
                        padding: "20px",
                        background: additionalServices.marketing ? "#FFF8F5" : "transparent",
                        borderRadius: "12px",
                        cursor: "pointer",
                        border: additionalServices.marketing ? "1px solid #FFA146" : "1px solid #FFEEDC",
                        boxShadow: additionalServices.marketing ? "0px 2px 8px rgba(102, 32, 45, 0.15)" : "none",
                        transition: "all 0.3s ease",
                      }}
                      onClick={() => handleServiceToggle("marketing")}
                    >
                      <Radio
                        checked={additionalServices.marketing}
                        onChange={() => handleServiceToggle("marketing")}
                        sx={{ "& .MuiSvgIcon-root": { fontSize: "26px", color: additionalServices.marketing ? "#66202D" : "#B0B0B0" } }}
                        name="additional-service"
                      />
                      <Box>
                        <Typography
                          sx={{
                            fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                            fontSize: "18px",
                            fontWeight: 500,
                            color: additionalServices.marketing ? "#66202D" : "#333333",
                          }}
                        >
                          Marketing
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                            fontSize: "14px",
                            fontWeight: 400,
                            color: "#888888",
                            marginTop: "10px",
                          }}
                        >
                          1. Curated B2B Brand Positioning <br />
                          2. Access to Regulatory-Backed Content Creation <br />
                          3. Global Outreach via Partner Channels <br />
                          4. Lead Generation & Deal Facilitation <br />
                          5. Thought Leadership Showcasing <br />
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: "15px",
                        padding: "20px",
                        background: additionalServices.manufacturing ? "#FFF8F5" : "transparent",
                        borderRadius: "12px",
                        cursor: "pointer",
                        border: additionalServices.manufacturing ? "1px solid #FFA146" : "1px solid #FFEEDC",
                        boxShadow: additionalServices.manufacturing ? "0px 2px 8px rgba(102, 32, 45, 0.15)" : "none",
                        transition: "all 0.3s ease",
                      }}
                      onClick={() => handleServiceToggle("manufacturing")}
                    >
                      <Radio
                        checked={additionalServices.manufacturing}
                        onChange={() => handleServiceToggle("manufacturing")}
                        sx={{ "& .MuiSvgIcon-root": { fontSize: "26px", color: additionalServices.manufacturing ? "#66202D" : "#B0B0B0" } }}
                        name="additional-service"
                      />
                      <Box>
                        <Typography
                          sx={{
                            fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                            fontSize: "18px",
                            fontWeight: 500,
                            color: additionalServices.manufacturing ? "#66202D" : "#333333",
                          }}
                        >
                          Manufacture
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                            fontSize: "14px",
                            fontWeight: 400,
                            color: "#888888",
                            marginTop: "10px",
                          }}
                        >
                          1. Trusted Contract Manufacturing Connections <br />
                          2. Tech Transfer & Scale-Up Support <br />
                          3. Ingredient Sourcing & Supply Chain Integration <br />
                          4. Regulatory-Compliant Production Guidance <br />
                          5. Innovation Infrastructure Access <br />
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "end", gap: "25px", marginTop: "40px" }}>
                  <Button
                    onClick={handleSkip}
                    sx={{
                      border: "2px solid #66202D",
                      borderRadius: "50px",
                      padding: "12px 40px",
                      fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "#66202D",
                      textTransform: "uppercase",
                      backgroundColor: "transparent",
                      "&:hover": {
                        backgroundColor: "#FFF8F5",
                        boxShadow: "0px 2px 8px rgba(102, 32, 45, 0.2)",
                      },
                    }}
                  >
                    Skip
                  </Button>
                  <Button
                    onClick={handleNext}
                    sx={{
                      backgroundColor: "#66202D",
                      color: "#FFFFFF",
                      borderRadius: "50px",
                      padding: "12px 40px",
                      fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                      fontSize: "16px",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      boxShadow: "0px 2px 8px rgba(102, 32, 45, 0.3)",
                      "&:hover": {
                        backgroundColor: "#551A25",
                        boxShadow: "0px 4px 12px rgba(102, 32, 45, 0.4)",
                      },
                    }}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            )}

            {step === "paymentDetails" && (
              <Box sx={{ marginTop: "40px", width: "1170px", margin: "0 auto" }}>
                <Box
                  sx={{
                    background: "#FBFBFB",
                    borderRadius: "20px",
                    padding: "30px",
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
                    border: "1px solid #E0E0E0",
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                    alignItems: "center",
                  }}
                >
                  {selectedClaimDetails.length > 0 && selectedClaimDetails.map((claim, index) => (
                    <Box key={claim.id || `claim-${index}`}>
                      <Grid container spacing={2}>
                        {/* Left Column */}
                        <Grid item xs={6}>
                          {/* Grade */}
                          <Typography
                            sx={{
                              fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                              fontSize: "14px",
                              fontWeight: 400,
                              color: "#AEA9A6",
                              marginBottom: "4px",
                            }}
                          >
                            Grade
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                              fontSize: "18px",
                              fontWeight: 400,
                              color: "#1E1E1E",
                              marginBottom: "4px",
                            }}
                          >
                            {claim.grade || "N/A"}
                          </Typography>
                          <Divider sx={{ borderColor: "#FFC173", margin: "8px 0" }} />

                          {/* Selected formula (conditional) */}
                          {selectedOption === "bookNow" && (
                            <>
                              <Typography
                                sx={{
                                  fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                                  fontSize: "14px",
                                  fontWeight: 400,
                                  color: "#AEA9A6",
                                  marginBottom: "4px",
                                }}
                              >
                                Selected formula
                              </Typography>
                              <Typography
                                sx={{
                                  fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                                  fontSize: "18px",
                                  fontWeight: 400,
                                  color: "#1E1E1E",
                                  marginBottom: "4px",
                                }}
                              >
                                Block the formula for a limited time (with a booking amount)
                              </Typography>
                              <Divider sx={{ borderColor: "#FFC173", margin: "8px 0" }} />
                            </>
                          )}

                          {/* Base Amount */}
                          <Typography
                            sx={{
                              fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                              fontSize: "14px",
                              fontWeight: 400,
                              color: "#AEA9A6",
                              marginBottom: "4px",
                            }}
                          >
                            Base Amount
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                              fontSize: "18px",
                              fontWeight: 400,
                              color: "#1E1E1E",
                              marginBottom: "4px",
                            }}
                          >
                            ₹{(parseFloat(claim.amount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Typography>
                          <Divider sx={{ borderColor: "#FFC173", margin: "8px 0" }} />

                          {/* Extended blocking period payment (conditional) */}
                          {selectedOption === "bookNow" && (
                            <>
                              <Typography
                                sx={{
                                  fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                                  fontSize: "14px",
                                  fontWeight: 400,
                                  color: "#AEA9A6",
                                  marginBottom: "4px",
                                }}
                              >
                                Extended blocking period payment
                              </Typography>
                              <Typography
                                sx={{
                                  fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                                  fontSize: "18px",
                                  fontWeight: 400,
                                  color: "#1E1E1E",
                                  marginBottom: "4px",
                                }}
                              >
                                ₹{(parseFloat(claim.booking_fee) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </Typography>
                              <Divider sx={{ borderColor: "#FFC173", margin: "8px 0" }} />
                            </>
                          )}

                          {/* GST (18%) */}
                          <Typography
                            sx={{
                              fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                              fontSize: "14px",
                              fontWeight: 400,
                              color: "#AEA9A6",
                              marginBottom: "4px",
                            }}
                          >
                            GST (18%)
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                              fontSize: "18px",
                              fontWeight: 400,
                              color: "#1E1E1E",
                              marginBottom: "4px",
                            }}
                          >
                            ₹{(parseFloat(gstAmount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Typography>
                          <Divider sx={{ borderColor: "#FFC173", margin: "8px 0" }} />
                        </Grid>

                        {/* Right Column */}
                        <Grid item xs={6}>
                          {/* Label Claim */}
                          <Typography
                            sx={{
                              fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                              fontSize: "14px",
                              fontWeight: 400,
                              color: "#AEA9A6",
                              marginBottom: "4px",
                            }}
                          >
                            Label Claim
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                              fontSize: "18px",
                              fontWeight: 400,
                              color: "#1E1E1E",
                              marginBottom: "4px",
                            }}
                          >
                            {claim.label_claim || "N/A"}
                          </Typography>
                          <Divider sx={{ borderColor: "#FFC173", margin: "8px 0" }} />

                          {/* Service name */}
                          <Typography
                            sx={{
                              fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                              fontSize: "14px",
                              fontWeight: 400,
                              color: "#AEA9A6",
                              marginBottom: "4px",
                            }}
                          >
                            Service name
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                              fontSize: "18px",
                              fontWeight: 400,
                              color: "#1E1E1E",
                              marginBottom: "4px",
                            }}
                          >
                            {additionalServices.manufacturing ? "Manufacture" : "Marketing"}
                          </Typography>
                          <Divider sx={{ borderColor: "#FFC173", margin: "8px 0" }} />

                          {/* Total Amount */}
                          <Typography
                            sx={{
                              fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                              fontSize: "14px",
                              fontWeight: 400,
                              color: "#AEA9A6",
                              marginBottom: "4px",
                            }}
                          >
                            Total Amount
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                              fontSize: "18px",
                              fontWeight: 400,
                              color: "#1E1E1E",
                              marginBottom: "4px",
                            }}
                          >
                            ₹{(parseFloat(totalAmount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Typography>
                          <Divider sx={{ borderColor: "#FFC173", margin: "8px 0" }} />
                        </Grid>
                      </Grid>

                      
                    </Box>
                  ))}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        sx={{ "& .MuiSvgIcon-root": { fontSize: "26px", color: termsAccepted ? "#66202D" : "#B0B0B0" } }}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                          fontSize: "18px",
                          fontWeight: 500,
                          color: "#333333",
                        }}
                      >
                        Read{" "}
                        <a
                          href="/terms-and-conditions"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#66202D", textDecoration: "underline" }}
                        >
                          Terms and Conditions
                        </a>
                      </Typography>
                    }
                    sx={{ margin: 0, padding: "10px 15px", borderRadius: "8px", backgroundColor: termsAccepted ? "#FFF8F5" : "transparent" }}
                  />
                </Box>
                <Box sx={{ display: "flex", justifyContent: "end", gap: "25px", marginTop: "40px" }}>
                  <Button
                    onClick={handleCancel}
                    sx={{
                      border: "2px solid #66202D",
                      borderRadius: "50px",
                      padding: "12px 40px",
                      fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "#66202D",
                      textTransform: "uppercase",
                      backgroundColor: "transparent",
                      "&:hover": {
                        backgroundColor: "#FFF8F5",
                        boxShadow: "0px 2px 8px rgba(102, 32, 45, 0.2)",
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handlePayment(false)}
                    disabled={isPaymentLoading || !termsAccepted}
                    sx={{
                      backgroundColor: !termsAccepted ? "#CCCCCC" : "#66202D",
                      color: "#FFFFFF",
                      borderRadius: "50px",
                      padding: "12px 40px",
                      fontFamily: "'PP Neue Montreal', 'Inter', sans-serif",
                      fontSize: "16px",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      boxShadow: !termsAccepted ? "none" : "0px 2px 8px rgba(102, 32, 45, 0.3)",
                      "&:hover": {
                        backgroundColor: !termsAccepted ? "#CCCCCC" : "#551A25",
                        boxShadow: !termsAccepted ? "none" : "0px 4px 12px rgba(102, 32, 45, 0.4)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    {isPaymentLoading && <CircularProgress size={20} color="inherit" />}
                    Pay Now
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      <Box
        sx={{
          padding: "20px 136px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: "auto",
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Lato', sans-serif",
            fontSize: "18px",
            fontWeight: 400,
            color: "#1E1E1E",
          }}
        >
          © 2025 DealSphere. All rights reserved.
        </Typography>

        <Box>
          <Button
            sx={{
              color: "#1E1E1E",
              fontFamily: "'Lato', sans-serif",
              fontSize: "18px",
              fontWeight: 400,
              textTransform: "none",
              mr: 2,
            }}
          >
            Terms
          </Button>
          <Button
            sx={{
              color: "#1E1E1E",
              fontFamily: "'Lato', sans-serif",
              fontSize: "18px",
              fontWeight: 400,
              textTransform: "none",
              mr: 2,
            }}
          >
            Privacy
          </Button>
          <Button
            sx={{
              color: "#1E1E1E",
              fontFamily: "'Lato', sans-serif",
              fontSize: "18px",
              fontWeight: 400,
              textTransform: "none",
            }}
          >
            Contact
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default Payment;